import { convertDotcmsResponseToRecord } from '../converters-from-dotcms';
import type { IRelatedResourcesData } from './dotcms-article';
import { ArticleGqlHelper } from './dotcms-article';
import { FileResourceWithThumbnailGqlHelper } from './resource-with-thumbnail/dotcms-file-resource-with-thumbnail';
import { UrlResourceWithThumbnailGqlHelper } from './resource-with-thumbnail/dotcms-url-resource-with-thumbnail';
import { buildGqlFieldsQuery } from '../gql-builders';
import type { IGqlHelper } from '../gql-helper';

export const areRelatedResourcesEmpty = (
  resources: Partial<IRelatedResourcesData>,
) =>
  !resources.articlesAsArticles?.length &&
  !resources.articlesAsExternalFiles?.length &&
  !resources.articlesAsExternalLinks?.length;

export const getRelatedResourcesCount = (
  resources: Partial<IRelatedResourcesData>,
) =>
  (resources.articlesAsArticles?.length ?? 0) +
  (resources.articlesAsExternalFiles?.length ?? 0) +
  (resources.articlesAsExternalLinks?.length ?? 0);

function extractResources<T>(
  resourcesFromCms: unknown,
  gqlHelper: IGqlHelper<T>,
) {
  return Array.isArray(resourcesFromCms)
    ? resourcesFromCms
      .map((resource) => gqlHelper.processGqlData(resource))
      .filter((maybeData): maybeData is T => maybeData !== undefined)
    : [];
}

export const RelatedResourcesDataGqlHelper: IGqlHelper<IRelatedResourcesData> =
  {
    contentTypeName: null,
    gqlFieldsQuery: `
    allArticlesArticleContentType {${buildGqlFieldsQuery(ArticleGqlHelper)}}
    allArticlesExternalFileContentType {${buildGqlFieldsQuery(
    FileResourceWithThumbnailGqlHelper,
  )}}
    allArticlesExternalUrlContentType {${buildGqlFieldsQuery(
    UrlResourceWithThumbnailGqlHelper,
  )}}
  `,
    processGqlData: (gqlResponse: unknown) => {
      if (!gqlResponse) {
        return undefined;
      }

      const {
        allArticlesArticleContentType,
        allArticlesExternalFileContentType,
        allArticlesExternalUrlContentType,
      } = convertDotcmsResponseToRecord(gqlResponse);

      const articlesAsArticles = extractResources(
        allArticlesArticleContentType,
        ArticleGqlHelper,
      );
      const articlesAsExternalFiles = extractResources(
        allArticlesExternalFileContentType,
        FileResourceWithThumbnailGqlHelper,
      );
      const articlesAsExternalLinks = extractResources(
        allArticlesExternalUrlContentType,
        UrlResourceWithThumbnailGqlHelper,
      );

      return {
        articlesAsArticles,
        articlesAsExternalFiles,
        articlesAsExternalLinks,
      };
    },
  };
